import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Zap, BadgeCheck, CreditCard, Mail, MessageSquare, Target,
  Bell, BookOpen, FolderOpen, BrainCircuit, Users, ShoppingBag,
  Lock, ArrowUpRight, Shield,
} from "lucide-react";
import Header from "../component/header";
import Footer from "../component/Footer";
import Banner from "../component/Banner";
import BannerImg from "../image/gallaryBanner.png";
import TierBadge from "../component/access/TierBadge";
import { UserDataContext } from "../context/User";
import CompletedEducation from "../component/CompletedEducation";

const TIER_GRADIENTS = {
  free:     "from-gray-400 to-gray-500",
  wanderer: "from-cyan-400 to-blue-500",
  kindred:  "from-purple-400 to-purple-600",
  quester:  "from-amber-400 to-orange-500",
  pilgrim:  "from-emerald-400 to-teal-600",
  guardian: "from-rose-400 to-red-600",
  spire:    "from-yellow-400 to-amber-600",
};

const TIER_ORDER = ["free","wanderer","kindred","quester","pilgrim","guardian","spire"];
const PROFILE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const PROFILE_IMAGE_MAX_SIZE = 5 * 1024 * 1024;

const formatBytes = (bytes = 0) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Profile = () => {
  const { userData, setUserData, fetchUser, unreadNotificationCount, notifications } = useContext(UserDataContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileNotice, setProfileNotice] = useState({ type: "", message: "" });
  const [historyRange, setHistoryRange] = useState("30d");
  const [historyPage, setHistoryPage] = useState(1);
  const [profileHistory, setProfileHistory] = useState({
    logs: [],
    pagination: { currentPage: 1, totalPages: 1, total: 0 },
  });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [privacyData, setPrivacyData] = useState({ privacyConsent: {}, requests: [] });
  const [privacySaving, setPrivacySaving] = useState(false);
  const [privacyRequestSaving, setPrivacyRequestSaving] = useState(false);
  const [privacyForm, setPrivacyForm] = useState({
    dataProcessing: false,
    marketingConsent: false,
  });
  const [privacyRequestForm, setPrivacyRequestForm] = useState({
    requestType: "export",
    message: "",
  });

  const tierKey = (userData?.membershipTier?.key || userData?.primaryRoleKey || "free").toLowerCase();
  const gradientClass = TIER_GRADIENTS[tierKey] || TIER_GRADIENTS.free;
  const subStatus = userData?.subscription?.status;
  const isActive = !subStatus || ["active","trialing"].includes(subStatus) || tierKey === "free";
  const userRank = TIER_ORDER.indexOf(tierKey);
  const meetsMin = (min) => !min || userRank >= TIER_ORDER.indexOf(min);
  const demographic = useMemo(() => userData?.demographic || {}, [userData?.demographic]);
  const initialProfile = useMemo(() => ({
    fullName: [userData?.firstName, userData?.lastName].filter(Boolean).join(" ").trim(),
    preferredName: userData?.preferredName || "",
    email: userData?.email || "",
    howHeard: userData?.howHeard || "",
    hopesForJoining: userData?.hopesForJoining || "",
    ageRange: demographic.ageRange || "",
    dateOfBirth: demographic.dateOfBirth ? new Date(demographic.dateOfBirth).toISOString().slice(0, 10) : "",
    locationType: demographic.locationType || "",
    genderIdentity: demographic.genderIdentity || "",
    ethnicity: demographic.ethnicity || "",
    urmIdentification:
      typeof demographic.urmIdentification === "boolean"
        ? String(demographic.urmIdentification)
        : "",
  }), [userData, demographic]);
  const [profileForm, setProfileForm] = useState(initialProfile);
  const [profileImage, setProfileImage] = useState(null);

  useEffect(() => {
    if (!userData) navigate("/login");
  }, [navigate, userData]);

  useEffect(() => {
    const status = new URLSearchParams(location.search).get("emailVerified");
    if (!status) return;

    if (status === "success") {
      setProfileNotice({ type: "success", message: "Email address verified and updated." });
      fetchUser?.();
    } else if (status === "taken") {
      setProfileNotice({ type: "error", message: "That email address is already in use." });
    } else if (status === "invalid") {
      setProfileNotice({ type: "error", message: "Email verification link is invalid or expired." });
    }
  }, [fetchUser, location.search]);

  useEffect(() => {
    if (!userData) return;

    let isMounted = true;
    setHistoryLoading(true);
    axios
      .get(`/api/auth/profile/history?range=${historyRange}&page=${historyPage}&limit=5`, {
        withCredentials: true,
      })
      .then(({ data }) => {
        if (!isMounted || !data.success) return;
        setProfileHistory({
          logs: data.logs || [],
          pagination: data.pagination || { currentPage: 1, totalPages: 1, total: 0 },
        });
      })
      .catch(() => {
        if (isMounted) {
          setProfileHistory({ logs: [], pagination: { currentPage: 1, totalPages: 1, total: 0 } });
        }
      })
      .finally(() => {
        if (isMounted) setHistoryLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [historyPage, historyRange, userData]);

  useEffect(() => {
    if (!userData) return;

    let isMounted = true;
    axios
      .get("/api/auth/privacy", { withCredentials: true })
      .then(({ data }) => {
        if (!isMounted || !data.success) return;
        const privacyConsent = data.privacyConsent || {};
        setPrivacyData({
          privacyConsent,
          requests: data.requests || [],
        });
        setPrivacyForm({
          dataProcessing: !!privacyConsent.dataProcessing,
          marketingConsent: !!privacyConsent.marketingConsent,
        });
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [userData]);

  if (!userData) {
    return null;
  }

  const formatDate = (value) => {
    if (!value) return "Not provided";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
  };
  const formatDateTime = (value) => {
    if (!value) return "Not available";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };
  const actionLabel = (action) => ({
    PROFILE_UPDATED: "Profile updated",
    PROFILE_EMAIL_CHANGE_REQUESTED: "Email change requested",
    PROFILE_EMAIL_VERIFIED: "Email verified",
    PROFILE_CHECKSUM_REPAIRED: "Profile checksum verified",
  }[action] || action);
  const demographicRows = [
    { label: "Age Range", value: demographic.ageRange },
    { label: "Date of Birth", value: formatDate(demographic.dateOfBirth) },
    { label: "Location Type", value: demographic.locationType },
    { label: "Gender Identity", value: demographic.genderIdentity },
    { label: "Ethnicity", value: demographic.ethnicity },
    {
      label: "Underrepresented?",
      value:
        typeof demographic.urmIdentification === "boolean"
          ? demographic.urmIdentification ? "Yes" : "No"
          : demographic.urmIdentification,
    },
  ];

  const stats = [
    { label: "Growth Sparks", value: userData?.growthSparks || 0, Icon: Zap,        iconColor: "text-amber-500", bg: "bg-amber-50" },
    { label: "Badges",        value: userData?.badges?.length || 0, Icon: BadgeCheck, iconColor: "text-cyan-500",  bg: "bg-cyan-50"  },
    { label: "Member ID",     value: userData?.memberId || "—",     Icon: CreditCard, iconColor: "text-gray-500",  bg: "bg-gray-50"  },
  ];
  const completedEducation = userData?.completedEducationActivities || userData?.educationActivities || [];
  const userFullName = [userData?.firstName, userData?.lastName].filter(Boolean).join(" ").trim();
  const displayName = userData?.preferredName || userFullName || userData?.memberId || "Member";
  const avatarInitial = (displayName || "M").charAt(0).toUpperCase();

  const startEditing = () => {
    setProfileForm(initialProfile);
    setProfileImage(null);
    setProfileNotice({ type: "", message: "" });
    setIsEditing(true);
  };

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((current) => ({ ...current, [name]: value }));
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setProfileNotice({ type: "", message: "" });

    if (!file) {
      setProfileImage(null);
      return;
    }

    if (!PROFILE_IMAGE_TYPES.includes(file.type)) {
      event.target.value = "";
      setProfileImage(null);
      setProfileNotice({
        type: "error",
        message: "Please upload a JPG, PNG, or WEBP profile image.",
      });
      return;
    }

    if (file.size > PROFILE_IMAGE_MAX_SIZE) {
      event.target.value = "";
      setProfileImage(null);
      setProfileNotice({
        type: "error",
        message: `Profile image is too large. Maximum size is ${formatBytes(PROFILE_IMAGE_MAX_SIZE)}.`,
      });
      return;
    }

    setProfileImage(file);
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    setProfileNotice({ type: "", message: "" });

    try {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const cleanEmail = profileForm.email.trim().toLowerCase();

      if (!emailPattern.test(cleanEmail)) {
        throw new Error("Please enter a valid email address.");
      }

      const formData = new FormData();
      ["fullName", "preferredName", "email", "howHeard", "hopesForJoining"].forEach((key) => {
        formData.append(key, key === "email" ? cleanEmail : profileForm[key] || "");
      });
      formData.append("demographic", JSON.stringify({
        ageRange: profileForm.ageRange,
        dateOfBirth: profileForm.dateOfBirth || null,
        locationType: profileForm.locationType,
        genderIdentity: profileForm.genderIdentity,
        ethnicity: profileForm.ethnicity,
        urmIdentification: profileForm.urmIdentification === "" ? undefined : profileForm.urmIdentification === "true",
      }));
      if (profileImage) formData.append("pfp", profileImage);

      const { data } = await axios.put("/api/auth/profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      if (data.success) {
        setUserData(data.user);
        setIsEditing(false);
        setProfileNotice({
          type: "success",
          message: data.message || "Profile updated successfully.",
        });
      }
    } catch (error) {
      setProfileNotice({
        type: "error",
        message: error?.response?.data?.message || error?.message || "Could not update profile. Please try again.",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const savePrivacyConsent = async () => {
    setPrivacySaving(true);
    setProfileNotice({ type: "", message: "" });

    try {
      const { data } = await axios.post("/api/auth/privacy/consent", privacyForm, {
        withCredentials: true,
      });
      if (data.success) {
        setPrivacyData((current) => ({
          ...current,
          privacyConsent: data.privacyConsent || {},
        }));
        setProfileNotice({ type: "success", message: data.message || "Privacy preferences updated." });
      }
    } catch (error) {
      setProfileNotice({
        type: "error",
        message: error?.response?.data?.message || "Could not update privacy preferences.",
      });
    } finally {
      setPrivacySaving(false);
    }
  };

  const submitPrivacyRequest = async (event) => {
    event.preventDefault();
    setPrivacyRequestSaving(true);
    setProfileNotice({ type: "", message: "" });

    try {
      const { data } = await axios.post("/api/auth/privacy/request", privacyRequestForm, {
        withCredentials: true,
      });
      if (data.success) {
        setPrivacyData((current) => ({
          ...current,
          requests: [data.request, ...(current.requests || [])],
        }));
        setPrivacyRequestForm({ requestType: "export", message: "" });
        setProfileNotice({ type: "success", message: data.message || "Privacy request submitted." });
      }
    } catch (error) {
      setProfileNotice({
        type: "error",
        message: error?.response?.data?.message || "Could not submit privacy request.",
      });
    } finally {
      setPrivacyRequestSaving(false);
    }
  };

  const quickLinks = [
    { label: "Quests",      path: "/quests",      Icon: Target,       minTier: null,       color: "text-blue-500",   bg: "bg-blue-50"   },
    { label: "Projects",    path: "/projects",    Icon: FolderOpen,   minTier: "kindred",  color: "text-purple-500", bg: "bg-purple-50" },
    { label: "Quiz",        path: "/quiz",         Icon: BrainCircuit, minTier: "wanderer", color: "text-green-500",  bg: "bg-green-50"  },
    { label: "Education",   path: "/education",    Icon: BookOpen,     minTier: null,       color: "text-indigo-500", bg: "bg-indigo-50" },
    { label: "Community",   path: "/community",    Icon: Users,        minTier: null,       color: "text-teal-500",   bg: "bg-teal-50"   },
    { label: "Shop",        path: "/shop",         Icon: ShoppingBag,  minTier: null,       color: "text-rose-500",   bg: "bg-rose-50"   },
  ];

  return (
    <div>
      <Header />
      <Banner backgroundImage={BannerImg} heading="My Profile" minHeight="40vh" padding="80px 0" />

      <section className="main-lwbl bg-[#F9FAFB] py-16 px-4 rounded-[30px]">
        <div className="max-w-4xl mx-auto">

          {/* Profile Card */}
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden mb-8">
            <div className={`h-28 bg-gradient-to-r ${gradientClass} opacity-80`} />

            <div className="px-6 pb-6 relative">
              {/* Avatar row */}
              <div className="flex items-end gap-4 -mt-7 mb-4">
                <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${gradientClass} flex items-center justify-center border-4 border-white shadow-lg flex-shrink-0 overflow-hidden`}>
                  {userData?.pfp ? (
                    <img src={userData.pfp} alt={displayName} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-bold font-cinzel text-3xl select-none">
                      {avatarInitial}
                    </span>
                  )}
                </div>
                <div className="pb-1">
                  <h2 className="font-cinzel text-2xl font-bold text-gray-900">
                    {displayName}
                  </h2>
                  <p className="text-gray-400 font-outfit text-sm">Member ID: {userData?.memberId || "Pending"}</p>
                  <div className="mt-1.5">
                    <TierBadge tierKey={tierKey} size="lg" />
                  </div>
                </div>
              </div>

              {/* Inactive warning */}
              {!isActive && (
                <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-2">
                  <Shield size={16} className="text-amber-500 flex-shrink-0" />
                  <p className="font-outfit text-sm text-amber-700">
                    Your subscription is inactive.{" "}
                    <button onClick={() => navigate("/membership")}
                      className="font-semibold underline hover:text-amber-900">
                      Renew now
                    </button>
                  </p>
                </div>
              )}

              <div className="mb-4 rounded-3xl border border-gray-100 bg-cyan-50/80 px-5 py-4 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-cyan-700 shadow-sm">
                      <Bell size={20} />
                    </div>
                    <div>
                      <p className="font-cinzel text-lg font-bold text-gray-900">Messages & notifications</p>
                      <p className="mt-1 text-sm text-gray-600 font-outfit">
                        {unreadNotificationCount > 0
                          ? `You have ${unreadNotificationCount} unread ${unreadNotificationCount === 1 ? 'update' : 'updates'}.`
                          : "No unread notifications. Check your inbox for recent updates."}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/notifications")}
                    className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-600"
                  >
                    <MessageSquare size={16} />
                    View notifications
                  </button>
                </div>
              </div>

              <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-cinzel text-lg font-bold text-gray-900">Recent messages</p>
                    <p className="mt-1 text-sm text-gray-500">Latest updates and messages from LWBL.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/notifications")}
                    className="rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-600"
                  >
                    View all
                  </button>
                </div>
                {notifications?.length > 0 ? (
                  <div className="space-y-3">
                    {notifications.slice(0, 3).map((notification) => (
                      <button
                        key={notification.id}
                        type="button"
                        onClick={() => navigate(notification.link.startsWith("/") ? notification.link.replace(/^\/lwbl/, "") : "/notifications")}
                        className="w-full rounded-2xl border border-gray-100 px-4 py-3 text-left transition hover:border-cyan-200 hover:bg-cyan-50"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-outfit text-sm font-semibold text-gray-900">{notification.title}</p>
                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">{notification.message}</p>
                          </div>
                          <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${notification.readAt ? "bg-gray-100 text-gray-500" : "bg-cyan-100 text-cyan-700"}`}>
                            {notification.readAt ? "Seen" : "New"}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                    No recent messages yet. Your latest notifications will show here once they arrive.
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {stats.map((s) => (
                  <div key={s.label} className="text-center p-4 bg-gray-50 rounded-2xl">
                    <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                      <s.Icon size={18} className={s.iconColor} strokeWidth={2} />
                    </div>
                    <p className="font-bold text-gray-900 font-outfit text-sm">{s.value}</p>
                    <p className="text-xs text-gray-400 font-outfit">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Info rows */}
              {profileNotice.message && (
                <div className={`mb-4 rounded-2xl border px-4 py-3 font-outfit text-sm ${
                  profileNotice.type === "error"
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-emerald-200 bg-emerald-50 text-emerald-700"
                }`}>
                  {profileNotice.message}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {userData?.email && (
                  <div className="flex items-center gap-2.5 text-sm text-gray-600 font-outfit">
                    <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail size={13} className="text-gray-400" />
                    </div>
                    <span className="truncate">{userData.email}</span>
                  </div>
                )}
                {userData?.pendingEmail && (
                  <div className="flex items-center gap-2.5 text-sm text-amber-700 font-outfit">
                    <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail size={13} className="text-amber-500" />
                    </div>
                    <span className="truncate">Pending email verification: {userData.pendingEmail}</span>
                  </div>
                )}
                {userData?.howHeard && (
                  <div className="flex items-center gap-2.5 text-sm text-gray-600 font-outfit">
                    <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare size={13} className="text-gray-400" />
                    </div>
                    <span>Found us via {userData.howHeard}</span>
                  </div>
                )}
                {userData?.hopesForJoining && (
                  <div className="flex items-center gap-2.5 text-sm text-gray-600 font-outfit col-span-2">
                    <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Target size={13} className="text-gray-400" />
                    </div>
                    <span>{userData.hopesForJoining}</span>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={startEditing}
                  className="rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-cyan-600"
                >
                  Edit Profile
                </button>
              </div>

              <div className="mt-6 rounded-3xl bg-slate-50 border border-slate-200 p-5">
                <h3 className="font-cinzel text-lg text-gray-900 mb-4">Member Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-gray-500">Tier</div>
                    <div className="mt-1 font-semibold text-gray-900">
                      {userData?.membershipTier?.title || userData?.primaryRoleKey || "Free"}
                    </div>
                  </div>
                  {demographicRows.map((row) => (
                    <div key={row.label}>
                      <div className="text-xs uppercase tracking-[0.22em] text-gray-500">{row.label}</div>
                      <div className="mt-1 font-semibold text-gray-900">
                        {row.value || "Not provided"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {isEditing && (
            <form onSubmit={saveProfile} className="mb-8 rounded-3xl border border-gray-100 bg-white p-6 shadow-lg">
              <div className="mb-5 flex items-center justify-between gap-4">
                <h3 className="font-cinzel text-xl font-bold text-gray-900">Edit Profile Information</h3>
                <button type="button" onClick={() => setIsEditing(false)} className="font-outfit text-sm text-gray-500 hover:text-gray-900">
                  Cancel
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  ["fullName", "Full name"],
                  ["preferredName", "Preferred name"],
                  ["email", "Email"],
                  ["howHeard", "How you heard about us"],
                  ["ageRange", "Age range"],
                  ["locationType", "Location type"],
                  ["genderIdentity", "Gender identity"],
                  ["ethnicity", "Ethnicity"],
                ].map(([name, label]) => (
                  <label key={name} className="font-outfit text-sm text-gray-600">
                    {label}
                    <input
                      name={name}
                      type={name === "email" ? "email" : "text"}
                      value={profileForm[name]}
                      onChange={handleProfileChange}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-cyan-400"
                    />
                  </label>
                ))}
                <label className="font-outfit text-sm text-gray-600">
                  Date of birth
                  <input
                    name="dateOfBirth"
                    type="date"
                    value={profileForm.dateOfBirth}
                    onChange={handleProfileChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-cyan-400"
                  />
                </label>
                <label className="font-outfit text-sm text-gray-600">
                  Underrepresented?
                  <select
                    name="urmIdentification"
                    value={profileForm.urmIdentification}
                    onChange={handleProfileChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-cyan-400"
                  >
                    <option value="">Prefer not to say</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <label className="font-outfit text-sm text-gray-600 sm:col-span-2">
                  Profile image
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleProfileImageChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                  />
                  <span className="mt-2 block text-xs text-gray-500">
                    JPG, PNG, or WEBP. Maximum size {formatBytes(PROFILE_IMAGE_MAX_SIZE)}.
                  </span>
                  {profileImage && (
                    <span className="mt-2 flex flex-wrap items-center gap-2 rounded-2xl border border-cyan-100 bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
                      <span className="font-semibold">{profileImage.name}</span>
                      <span>{formatBytes(profileImage.size)}</span>
                    </span>
                  )}
                </label>
                <label className="font-outfit text-sm text-gray-600 sm:col-span-2">
                  Hopes for joining
                  <textarea
                    name="hopesForJoining"
                    value={profileForm.hopesForJoining}
                    onChange={handleProfileChange}
                    rows={3}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-cyan-400"
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={savingProfile}
                className="mt-5 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] px-6 py-3 font-outfit text-sm font-semibold text-black disabled:opacity-60"
              >
                {savingProfile ? "Saving..." : "Save Profile"}
              </button>
            </form>
          )}

          <div className="mb-8 rounded-3xl border border-gray-100 bg-white p-6 shadow-lg">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="font-cinzel text-xl font-bold text-gray-900">Profile Activity</h3>
              <select
                value={historyRange}
                onChange={(event) => {
                  setHistoryRange(event.target.value);
                  setHistoryPage(1);
                }}
                className="rounded-2xl border border-gray-200 px-4 py-2 font-outfit text-sm outline-none focus:border-cyan-400"
              >
                <option value="30d">Last 30 days</option>
                <option value="6m">Last 6 months</option>
                <option value="all">All</option>
              </select>
            </div>

            {historyLoading ? (
              <p className="font-outfit text-sm text-gray-500">Loading activity...</p>
            ) : profileHistory.logs.length ? (
              <div className="space-y-3">
                {profileHistory.logs.map((log) => (
                  <div key={log.id} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <p className="font-outfit text-sm font-semibold text-gray-900">{actionLabel(log.action)}</p>
                      <p className="font-outfit text-xs text-gray-500">{formatDateTime(log.createdAt)}</p>
                    </div>
                    {log.metadata?.changedFields?.length > 0 && (
                      <p className="mt-1 font-outfit text-xs text-gray-500">
                        Fields: {log.metadata.changedFields.join(", ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-outfit text-sm text-gray-500">No profile activity for this period.</p>
            )}

            {profileHistory.pagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  disabled={historyPage <= 1}
                  onClick={() => setHistoryPage((page) => Math.max(page - 1, 1))}
                  className="rounded-full border border-gray-200 px-4 py-2 font-outfit text-sm text-gray-700 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="font-outfit text-sm text-gray-500">
                  Page {profileHistory.pagination.currentPage} of {profileHistory.pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={historyPage >= profileHistory.pagination.totalPages}
                  onClick={() => setHistoryPage((page) => page + 1)}
                  className="rounded-full border border-gray-200 px-4 py-2 font-outfit text-sm text-gray-700 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          <div className="mb-8 rounded-3xl border border-gray-100 bg-white p-6 shadow-lg">
            <div className="mb-5">
              <h3 className="font-cinzel text-xl font-bold text-gray-900">Privacy & Data</h3>
              <p className="mt-1 font-outfit text-sm text-gray-500">
                Manage consent and submit data access, correction, or deletion requests.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <h4 className="font-outfit text-sm font-semibold text-gray-900">Consent Preferences</h4>
                <label className="mt-4 flex items-start gap-3 font-outfit text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={privacyForm.dataProcessing}
                    onChange={(event) =>
                      setPrivacyForm((current) => ({ ...current, dataProcessing: event.target.checked }))
                    }
                    className="mt-1"
                  />
                  <span>I consent to LWBL processing my account and profile data to provide site services.</span>
                </label>
                <label className="mt-3 flex items-start gap-3 font-outfit text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={privacyForm.marketingConsent}
                    onChange={(event) =>
                      setPrivacyForm((current) => ({ ...current, marketingConsent: event.target.checked }))
                    }
                    className="mt-1"
                  />
                  <span>I agree to receive optional updates and community announcements.</span>
                </label>
                <button
                  type="button"
                  onClick={savePrivacyConsent}
                  disabled={privacySaving}
                  className="mt-4 rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white disabled:opacity-60"
                >
                  {privacySaving ? "Saving..." : "Save Preferences"}
                </button>
                {privacyData.privacyConsent?.updatedAt && (
                  <p className="mt-3 font-outfit text-xs text-gray-500">
                    Last updated {formatDateTime(privacyData.privacyConsent.updatedAt)}
                  </p>
                )}
              </div>

              <form onSubmit={submitPrivacyRequest} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <h4 className="font-outfit text-sm font-semibold text-gray-900">Submit Data Request</h4>
                <label className="mt-4 block font-outfit text-sm text-gray-600">
                  Request type
                  <select
                    value={privacyRequestForm.requestType}
                    onChange={(event) =>
                      setPrivacyRequestForm((current) => ({ ...current, requestType: event.target.value }))
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-cyan-400"
                  >
                    <option value="export">Data export</option>
                    <option value="delete">Account/data deletion</option>
                    <option value="correction">Data correction</option>
                  </select>
                </label>
                <label className="mt-3 block font-outfit text-sm text-gray-600">
                  Details
                  <textarea
                    value={privacyRequestForm.message}
                    onChange={(event) =>
                      setPrivacyRequestForm((current) => ({ ...current, message: event.target.value }))
                    }
                    rows={3}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-cyan-400"
                    placeholder="Add context for the LWBL team"
                  />
                </label>
                <button
                  type="submit"
                  disabled={privacyRequestSaving}
                  className="mt-4 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] px-5 py-2.5 font-outfit text-sm font-semibold text-black disabled:opacity-60"
                >
                  {privacyRequestSaving ? "Submitting..." : "Submit Request"}
                </button>
              </form>
            </div>

            <div className="mt-5">
              <h4 className="mb-3 font-outfit text-sm font-semibold text-gray-900">Recent Privacy Requests</h4>
              {privacyData.requests?.length ? (
                <div className="space-y-2">
                  {privacyData.requests.map((request) => (
                    <div key={request.id} className="rounded-2xl border border-gray-100 bg-white px-4 py-3">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <p className="font-outfit text-sm font-semibold capitalize text-gray-900">
                          {request.requestType} request
                        </p>
                        <span className="font-outfit text-xs capitalize text-gray-500">
                          {String(request.status || "").replace("_", " ")}
                        </span>
                      </div>
                      <p className="mt-1 font-outfit text-xs text-gray-500">
                        Submitted {formatDateTime(request.createdAt)}
                        {request.dueAt ? ` - Due ${formatDate(request.dueAt)}` : ""}
                      </p>
                      {request.response && (
                        <p className="mt-1 font-outfit text-xs text-gray-600">Response: {request.response}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="font-outfit text-sm text-gray-500">No privacy requests submitted yet.</p>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <h3 className="font-cinzel text-xl font-bold text-gray-900 mb-4">Quick Access</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {quickLinks.map((link) => {
              const locked = !meetsMin(link.minTier) || (!isActive && link.minTier);
              return (
                <button
                  key={link.label}
                  onClick={() => navigate(locked ? "/membership" : link.path)}
                  className={`p-4 rounded-2xl border text-left transition-all duration-200 group
                    ${locked
                      ? "bg-gray-50 border-gray-200 hover:bg-amber-50 hover:border-amber-200"
                      : "bg-white border-gray-200 hover:border-cyan-300 hover:shadow-md hover:-translate-y-0.5"
                    }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 ${locked ? "bg-gray-100" : link.bg} rounded-xl flex items-center justify-center transition-colors`}>
                      <link.Icon
                        size={17}
                        className={locked ? "text-gray-300" : link.color}
                        strokeWidth={1.8}
                      />
                    </div>
                    {locked && <Lock size={13} className="text-amber-400" />}
                  </div>
                  <p className={`font-outfit font-semibold text-sm ${locked ? "text-gray-400" : "text-gray-800"}`}>
                    {link.label}
                  </p>
                  {locked && link.minTier && (
                    <p className="font-outfit text-xs text-amber-500 mt-0.5 capitalize">
                      {link.minTier}+ required
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          <CompletedEducation activities={completedEducation} compact />

          {/* Upgrade CTA for free users */}
          {tierKey === "free" && (
            <div className="mt-6 p-5 rounded-2xl bg-gradient-to-r from-[#B6FCFF]/40 to-[#00C3FF]/30 border border-cyan-200 flex items-center justify-between gap-4">
              <div>
                <p className="font-cinzel font-bold text-gray-900 text-base mb-1">Unlock Full Access</p>
                <p className="font-outfit text-sm text-gray-600">
                  Upgrade to Wanderer or higher to access quests, quiz, projects and more.
                </p>
              </div>
              <button
                onClick={() => navigate("/membership")}
                className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-semibold font-outfit text-sm hover:scale-105 transition-transform shadow"
              >
                <ArrowUpRight size={15} strokeWidth={2.5} />
                Upgrade
              </button>
            </div>
          )}

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Profile;
